import { Button } from "@/components/ui/button"
import { useCreateChapter } from "@/hooks/useChapters"
import { useDialog } from "@/hooks/useDialog"
import { useProjectMeta } from "@/hooks/meta/useProjectMeta"
import { BulkChapterExport } from "@/pages/chapters/components/bulk-chapter-export"
import ChapterListComponent from "@/pages/chapters/components/chapter-list"
import { BookOpen, Download, Plus, X } from "lucide-react"
import { useParams } from "react-router-dom"
import { toast } from "sonner"

interface ChapterSidebarProps {
    onClose?: () => void;
}

export function ChapterSidebar({ onClose }: ChapterSidebarProps) {
    const { projectId } = useParams();
    const { name: projectName, currentStoryId } = useProjectMeta(projectId || null);
    const storyId = currentStoryId?.toString();
    const createChapterMutation = useCreateChapter();
    const { openDialog } = useDialog();

    const handleAddChapter = () => {
        if (!projectId || !storyId) {
            return;
        }

        openDialog('form', {
            title: "Create New Chapter",
            description: "Create a new chapter with description and outline",
            fields: [
                { name: "name", label: "Chapter Name", type: "text", placeholder: "Chapter 1: The Beginning", required: true },
                { name: "description", label: "Description / Outline", type: "textarea", placeholder: "Describe what happens in this chapter..." }
            ],
            submitText: "Create Chapter"
        }, async (data: { name: string; description: string }) => {
            try {
                await createChapterMutation.mutateAsync({
                    storyId: parseInt(storyId),
                    title: data.name,
                    description: data.description,
                    projectId
                });
                toast.success('Chapter created successfully');
            } catch (error) {
                console.error('Error creating chapter:', error);
                toast.error('Failed to create chapter');
            }
        });
    };

    return (
        <div className="h-full w-full flex flex-col">
            {/* Header */}
            <div className="flex flex-col gap-3 p-0 border-b">
                <div className="h-12 flex flex-row items-center justify-between px-4 py-0 shrink-0">
                    <div className="flex items-center gap-2 font-semibold text-sm">
                        <BookOpen className="h-4 w-4" />
                        <span>Manuscript Navigation</span>
                    </div>
                    {onClose && (
                        <button
                            onClick={onClose}
                            className="h-6 w-6 flex items-center justify-center rounded-md hover:bg-muted text-muted-foreground transition-colors"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-auto px-3 py-3">
                <div className="relative flex w-full min-w-0 flex-col">
                    <div className="w-full text-sm">
                        <ul className="flex w-full min-w-0 flex-col gap-2">
                            <div className="py-2 px-0">
                                <ChapterListComponent />
                            </div>
                        </ul>
                    </div>
                </div>
            </div>

            {/* Footer */}
            <div className="flex flex-col gap-2 p-4 border-t">
                {projectId && (
                    <BulkChapterExport
                        projectId={projectId}
                        projectName={projectName}
                        trigger={
                            <Button variant="outline" className="w-full justify-start">
                                <Download className="w-4 h-4 mr-2" />
                                Export Chapters
                            </Button>
                        }
                    />
                )}
                <Button onClick={handleAddChapter} className="w-full justify-start">
                    <Plus className="w-4 h-4 mr-2" />
                    Add Chapter
                </Button>
            </div>
        </div>
    )
}
